import React, { useState } from "react";
import { Container, Card, Row, Col, Form, Button, Spinner, Alert, Badge, Table } from "react-bootstrap";
import BotonManualHeader from "../../components/BotonManualHeader";
import { FaDownload, FaFileArchive, FaFilePdf } from "react-icons/fa";
import swal from "sweetalert2";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { getFirestore, collection, getDocs, query, where } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { exportarToPDF } from "../../functions/exportarToPDF";

const db = getFirestore(firebaseApp);
const QUERY_CONCURRENCY = 10;
const DOWNLOAD_CONCURRENCY = 6;

const TIPOS_DOCUMENTAL = [
  "Boletas de pago",
  "CTS",
  "Gratificaciones",
  "Utilidades",
  "Contratos",
  "Renta",
  "Memorandums",
  "Vacaciones",
  "Prestamos",
  "Adelantos",
  "reciboHonorarios",
  "ConstanciaFirma",
  "PoliticasLaborales",
  "Ceses",
  "Otros"
];

const mapWithConcurrency = async (items, limit, asyncMapper) => {
  const safeLimit = Math.max(1, limit || 1);
  const results = [];
  for (let i = 0; i < items.length; i += safeLimit) {
    const chunk = items.slice(i, i + safeLimit);
    const chunkResults = await Promise.all(chunk.map(asyncMapper));
    results.push(...chunkResults);
  }
  return results;
};

const normalizeDownloadUrl = (rawUrl) => {
  if (!rawUrl) return rawUrl;
  try {
    const parsed = new URL(rawUrl);
    const isStorageGoogleApis = parsed.hostname === "storage.googleapis.com";
    if (!isStorageGoogleApis) return rawUrl;

    const pathname = parsed.pathname.replace(/^\/+/, "");
    const [bucket, ...objectPathParts] = pathname.split("/");
    if (!bucket || objectPathParts.length === 0) return rawUrl;

    const objectPath = objectPathParts.join("/");
    const normalized = new URL(`https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(objectPath)}`);
    normalized.searchParams.set("alt", "media");
    return normalized.toString();
  } catch (e) {
    return rawUrl;
  }
};

const getColeccionByTipo = (tipo) => {
  if (tipo === "reciboHonorarios") return "uploadDocument";
  if (tipo === "ConstanciaFirma") return "firmasComunicado";
  return "document";
};

function DescargarDocumentosGestion({ usuario }) {
  const [tipoDocumento, setTipoDocumento] = useState("Boletas de pago");
  const [mes, setMes] = useState("0");
  const [ano, setAno] = useState(String(new Date().getFullYear()));
  const [loadingAction, setLoadingAction] = useState(null); // "zip" | "reporte" | null
  const [error, setError] = useState(null);
  const [registrosEncontrados, setRegistrosEncontrados] = useState([]);
  const [progresoBusqueda, setProgresoBusqueda] = useState({ procesados: 0, total: 0 });

  const obtenerUsuariosEmpresa = async (rucActual) => {
    const usersRef = collection(db, "users");
    let usersSnap = await getDocs(query(usersRef, where("rucEmpresa", "==", rucActual)));
    if (usersSnap.empty) {
      usersSnap = await getDocs(query(usersRef, where("ruc", "==", rucActual)));
    }

    const usuarios = [];
    usersSnap.forEach((userDoc) => {
      const data = userDoc.data();
      if (data.activo === false || data.activo === "false") return;
      usuarios.push({
        dni: data.dni || userDoc.id,
        colaborador: `${data.apellidos || ""} ${data.name || ""}`.trim() || userDoc.id,
        email: data.email || ""
      });
    });
    return usuarios;
  };

  const formatearMes = (value) => String(value).padStart(2, "0");

  const fechaATime = (fecha) => {
    if (!fecha) return 0;
    if (typeof fecha?.toDate === "function") return fecha.toDate().getTime();
    if (fecha?.seconds) return fecha.seconds * 1000;
    if (fecha instanceof Date) return fecha.getTime();
    return 0;
  };

  const obtenerRegistros = async () => {
    const rucActual = String(usuario?.ruc || "").trim();
    if (!rucActual) {
      swal.fire("Error", "No se encontró el RUC de la empresa.", "error");
      return null;
    }

    const usuarios = await obtenerUsuariosEmpresa(rucActual);
    if (!usuarios.length) return [];

    setRegistrosEncontrados([]);
    setProgresoBusqueda({ procesados: 0, total: usuarios.length });

    const coleccionDocs = getColeccionByTipo(tipoDocumento);
    const anoQuery = tipoDocumento === "reciboHonorarios" ? parseInt(ano, 10) : ano;
    const mesSeleccionado = mes;

    const registrosPorUsuario = await mapWithConcurrency(usuarios, QUERY_CONCURRENCY, async (trabajador) => {
      const docsRef = collection(db, coleccionDocs);
      const q = query(
        docsRef,
        where("tipo", "==", tipoDocumento),
        where("dni", "==", trabajador.dni),
        where("ano", "==", anoQuery)
      );

      const snap = await getDocs(q);
      const registros = [];
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        const mesRegistro = String(data.mes || data.periodo || "").padStart(2, "0");
        if (mesSeleccionado !== "0" && mesRegistro !== formatearMes(mesSeleccionado)) return;

        registros.push({
          id: docSnap.id,
          dni: trabajador.dni,
          colaborador: trabajador.colaborador,
          email: trabajador.email,
          tipo: data.tipo || tipoDocumento,
          tipoId: "-",
          periodo: mesRegistro || "-",
          ano: String(data.ano || ano),
          fechaDoc: data.fechaDoc || null,
          ruc: rucActual,
          contador: data.contador || 0,
          plataforma: data.plataforma || "web",
          fechaFirma: data.fechaFirma || null,
          fechaVista: data.fechaVista || null,
          link: data.link || data.url || null,
          linkFirmado: data.linkFirmado || null,
          nombre: data.nombre || data.name || "Documento"
        });
      });

      if (registros.length > 0) {
        const resumenUsuario = registros.reduce((acc, reg) => {
          if (!acc) return reg;
          return fechaATime(reg.fechaFirma || reg.fechaVista || reg.fechaDoc) >
            fechaATime(acc.fechaFirma || acc.fechaVista || acc.fechaDoc)
            ? reg
            : acc;
        }, null);

        setRegistrosEncontrados((prev) => {
          const sinUsuario = prev.filter((item) => item.dni !== trabajador.dni);
          return [...sinUsuario, resumenUsuario];
        });
      }

      setProgresoBusqueda((prev) => ({ ...prev, procesados: Math.min(prev.procesados + 1, prev.total) }));
      return registros;
    });

    return registrosPorUsuario.flat();
  };

  const extFrom = (url, contentType) => {
    if (contentType?.includes("pdf")) return "pdf";
    if (contentType?.includes("png")) return "png";
    if (contentType?.includes("jpeg") || contentType?.includes("jpg")) return "jpg";
    if (contentType?.includes("webp")) return "webp";
    try {
      const path = new URL(url).pathname;
      const file = decodeURIComponent(path.split("/").pop() || "");
      if (file.includes(".")) return file.split(".").pop().toLowerCase();
    } catch (e) {
      console.warn("No se pudo inferir extensión", e);
    }
    return "bin";
  };

  const sanitize = (text = "") =>
    text
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");

  const descargarZip = async () => {
    try {
      setLoadingAction("zip");
      setError(null);

      const registros = await obtenerRegistros();
      if (!registros) return;
      if (registros.length === 0) {
        swal.fire("Sin datos", `No se encontraron registros para ${tipoDocumento} en ${mes === "0" ? "todos los meses" : mes}/${ano}.`, "info");
        return;
      }

      const zip = new JSZip();
      const periodo = mes === "0" ? `${ano}_ALL` : `${ano}${formatearMes(mes)}`;
      const folder = zip.folder(`DOC_${sanitize(tipoDocumento)}_${periodo}`);
      let agregados = 0;

      const descargas = await mapWithConcurrency(
        registros.map((item, index) => ({ item, index })),
        DOWNLOAD_CONCURRENCY,
        async ({ item, index }) => {
          const rawLink = item.linkFirmado || item.link;
          const link = normalizeDownloadUrl(rawLink);
          if (!link) return null;
          try {
            const response = await fetch(link);
            if (!response.ok) return null;
            const blob = await response.blob();
            const ext = extFrom(link, blob.type);
            const name = `${String(index + 1).padStart(4, "0")}_${item.dni}_${sanitize(item.colaborador)}.${ext}`;
            return { name, blob };
          } catch (e) {
            console.warn("Error descargando archivo", e);
            return null;
          }
        }
      );

      descargas.forEach((descarga) => {
        if (!descarga) return;
        folder.file(descarga.name, descarga.blob);
        agregados += 1;
      });

      if (!agregados) {
        swal.fire("Sin archivos", "Se encontraron registros, pero no se pudo descargar archivos válidos.", "warning");
        return;
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const rucActual = String(usuario?.ruc || "").trim();
      saveAs(zipBlob, `Documental_${sanitize(tipoDocumento)}_${periodo}_${rucActual}.zip`);
      swal.fire("Completado", `ZIP generado con ${agregados} archivos.`, "success");
    } catch (e) {
      console.error(e);
      setError("Ocurrió un error al generar el ZIP.");
      swal.fire("Error", "No se pudo generar el ZIP.", "error");
    } finally {
      setLoadingAction(null);
      setProgresoBusqueda((prev) => ({ ...prev, procesados: prev.total }));
    }
  };

  const descargarReporte = async () => {
    try {
      setLoadingAction("reporte");
      setError(null);

      const registros = await obtenerRegistros();
      if (!registros) return;
      if (registros.length === 0) {
        swal.fire("Sin datos", `No se encontraron envíos para ${tipoDocumento} en ${mes === "0" ? "todos los meses" : mes}/${ano}.`, "info");
        return;
      }

      const rucActual = String(usuario?.ruc || "").trim();
      const pdfBytes = await exportarToPDF(registros, rucActual);
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const periodo = mes === "0" ? `${ano}_ALL` : `${ano}${formatearMes(mes)}`;
      a.href = url;
      a.download = `Reporte_Envio_${sanitize(tipoDocumento)}_${periodo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError("Ocurrió un error al generar el reporte.");
      swal.fire("Error", "No se pudo generar el reporte.", "error");
    } finally {
      setLoadingAction(null);
      setProgresoBusqueda((prev) => ({ ...prev, procesados: prev.total }));
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return "-";
    if (typeof fecha?.toDate === "function") return fecha.toDate().toLocaleString("es-PE", { timeZone: "America/Lima" });
    if (fecha?.seconds) return new Date(fecha.seconds * 1000).toLocaleString("es-PE", { timeZone: "America/Lima" });
    if (fecha instanceof Date) return fecha.toLocaleString("es-PE", { timeZone: "America/Lima" });
    return "-";
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}>
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
              borderBottom: "3px solid #f39c12",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">Descargar Documentos Gestión Documental</h2>
              <p className="mb-0 opacity-75">Descarga masiva por tipo de documento y reporte de envío</p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {error && <Alert variant="warning" className="border-0 shadow-sm mb-4">{error}</Alert>}

            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body>
                <h6 className="fw-bold text-dark mb-2">¿Qué puede hacer en este módulo?</h6>
                <p className="text-muted mb-2">
                  Este componente permite gestionar descargas masivas de documentos del módulo documental a nivel empresa, sin necesidad de buscar colaboradores por DNI.
                </p>
                <ul className="text-muted mb-2">
                  <li><strong>Descargar ZIP por tipo de documento:</strong> consolida en un solo archivo todos los registros encontrados por documento, mes y año.</li>
                  <li><strong>Descargar reporte de envío (PDF):</strong> genera un reporte ejecutivo con el detalle de envíos identificados en el periodo.</li>
                </ul>
                <p className="text-muted mb-0">
                  Este flujo está orientado a control operativo, auditoría documentaria y trazabilidad para áreas de RR.HH. y cumplimiento.
                </p>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px", backgroundColor: "#f8f9ff", border: "1px solid #e0e7ff" }}>
              <Card.Body className="py-3">
                <Row className="g-3 align-items-end">
                  <Col md={6}>
                    <Form.Label className="fw-semibold">Tipo de documento</Form.Label>
                    <Form.Select
                      value={tipoDocumento}
                      onChange={(e) => setTipoDocumento(e.target.value)}
                      disabled={!!loadingAction}
                      className="rounded-3 border-primary"
                    >
                      {TIPOS_DOCUMENTAL.map((tipo) => (
                        <option key={tipo} value={tipo}>{tipo}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Label className="fw-semibold">Mes</Form.Label>
                    <Form.Select value={mes} onChange={(e) => setMes(e.target.value)} disabled={!!loadingAction} className="rounded-3">
                      <option value="0">Todos</option>
                      <option value="1">Enero</option>
                      <option value="2">Febrero</option>
                      <option value="3">Marzo</option>
                      <option value="4">Abril</option>
                      <option value="5">Mayo</option>
                      <option value="6">Junio</option>
                      <option value="7">Julio</option>
                      <option value="8">Agosto</option>
                      <option value="9">Septiembre</option>
                      <option value="10">Octubre</option>
                      <option value="11">Noviembre</option>
                      <option value="12">Diciembre</option>
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Label className="fw-semibold">Año</Form.Label>
                    <Form.Select value={ano} onChange={(e) => setAno(e.target.value)} disabled={!!loadingAction} className="rounded-3">
                      <option value="2023">2023</option>
                      <option value="2024">2024</option>
                      <option value="2025">2025</option>
                      <option value="2026">2026</option>
                      <option value="2027">2027</option>
                    </Form.Select>
                  </Col>
                </Row>
                <Row className="g-3 mt-1">
                  <Col md={6}>
                    <Button variant="success" onClick={descargarZip} disabled={!!loadingAction} className="fw-bold w-100 py-2">
                      {loadingAction === "zip" ? <Spinner size="sm" animation="border" className="me-2" /> : <FaFileArchive className="me-2" />}
                      Descargar ZIP
                    </Button>
                  </Col>
                  <Col md={6}>
                    <Button variant="danger" onClick={descargarReporte} disabled={!!loadingAction} className="fw-bold w-100 py-2">
                      {loadingAction === "reporte" ? <Spinner size="sm" animation="border" className="me-2" /> : <FaFilePdf className="me-2" />}
                      Descargar Reporte
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
              <Card.Body>
                <div className="text-muted small">
                  <FaDownload className="me-1" />
                  Procesa toda la empresa usando el RUC actual y muestra solo usuarios con documento encontrado.
                </div>
                {(!!loadingAction || registrosEncontrados.length > 0) && (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h6 className="mb-1 fw-bold text-dark">Registros encontrados</h6>
                        <small className="text-muted">{registrosEncontrados.length} usuario(s) con coincidencias</small>
                      </div>
                      <small className="text-muted fw-semibold">
                        {progresoBusqueda.procesados}/{progresoBusqueda.total} colaboradores procesados
                      </small>
                    </div>
                    <div style={{ maxHeight: "360px", overflowY: "auto", border: "1px solid #e9ecef", borderRadius: "12px", background: "#fff" }}>
                      <Table hover responsive className="mb-0 align-middle">
                        <thead style={{ position: "sticky", top: 0, background: "#f4f7fb", zIndex: 1 }}>
                          <tr>
                            <th className="text-center text-secondary fw-semibold" style={{ width: "70px" }}>#</th>
                            <th>DNI</th>
                            <th>Colaborador</th>
                            <th>Documento</th>
                            <th>Fecha</th>
                            <th className="text-center">Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {registrosEncontrados.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="text-center text-muted py-4">
                                {!!loadingAction ? "Buscando registros..." : "Sin registros para los filtros seleccionados."}
                              </td>
                            </tr>
                          ) : (
                            registrosEncontrados.map((r, idx) => (
                              <tr key={`${r.dni}-${idx}`}>
                                <td className="text-center fw-bold text-primary">{idx + 1}</td>
                                <td className="fw-semibold">{r.dni}</td>
                                <td>{r.colaborador}</td>
                                <td>{r.tipo}</td>
                                <td>{formatearFecha(r.fechaFirma || r.fechaVista || r.fechaDoc)}</td>
                                <td className="text-center">
                                  {r.linkFirmado || r.link ? (
                                    <Badge bg="success" pill>Disponible</Badge>
                                  ) : (
                                    <Badge bg="secondary" pill>Sin enlace</Badge>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default DescargarDocumentosGestion;
